import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Star } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { AnalysisPanel } from "../components/AnalysisPanel";
import { QuestionView } from "../components/QuestionView";
import { Badge, Card, ErrorBanner, Spinner, cx } from "../components/ui";
import { api } from "../lib/api";
import { CARD_STATE_LABELS, formatDue, formatPercent } from "../lib/format";

export function QuestionDetailPage() {
    const { number: numberParam } = useParams<{ number: string }>();
    const number = Number.parseInt(numberParam ?? "", 10);
    const queryClient = useQueryClient();

    const detailQuery = useQuery({
        queryKey: ["question", number],
        queryFn: () => api.questions.get(number),
        enabled: Number.isInteger(number)
    });
    const settingsQuery = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });

    const starMutation = useMutation({
        mutationFn: (starred: boolean) => api.questions.star(number, starred),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ["question", number] });
            void queryClient.invalidateQueries({ queryKey: ["questions"] });
        }
    });

    if (!Number.isInteger(number)) return <ErrorBanner message="题号无效" />;
    if (detailQuery.isLoading) return <Spinner />;
    if (detailQuery.isError || !detailQuery.data) {
        return <ErrorBanner message="加载题目失败" onRetry={() => void detailQuery.refetch()} />;
    }

    const { question, card } = detailQuery.data;
    const settings = settingsQuery.data?.settings;

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between gap-3">
                <Link
                    to="/browse"
                    className="flex items-center gap-1.5 text-sm text-ink-soft transition-colors hover:text-ink"
                >
                    <ArrowLeft className="size-4" aria-hidden />
                    返回题库
                </Link>

                {card ? (
                    <button
                        onClick={() => starMutation.mutate(!card.isStarred)}
                        aria-pressed={card.isStarred}
                        className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft transition-colors hover:text-ink"
                    >
                        <Star className={cx("size-4", card.isStarred && "fill-warn text-warn")} aria-hidden />
                        {card.isStarred ? "已收藏" : "收藏"}
                    </button>
                ) : null}
            </div>

            <Card className="p-5 sm:p-7">
                {/* The detail page is a reference view, so the answer is always visible. */}
                <QuestionView
                    question={question}
                    revealed
                    showTranslation={settings?.showQuestionTranslation ?? false}
                />

                <div className="mt-7 border-t border-line pt-6">
                    <AnalysisPanel
                        analysis={question.analysis}
                        showEnglish={settings?.showEnglish ?? false}
                        showChinese={settings?.showChinese ?? true}
                    />
                </div>
            </Card>

            {card ? (
                <Card className="space-y-3 p-5">
                    <h2 className="text-sm font-semibold text-ink">你的复习状态</h2>
                    <div className="flex flex-wrap gap-2">
                        <Badge tone={card.state === "REVIEW" ? "good" : card.state === "NEW" ? "neutral" : "warn"}>
                            {CARD_STATE_LABELS[card.state] ?? card.state}
                        </Badge>
                        {card.state !== "NEW" ? <Badge>下次复习：{formatDue(card.due)}</Badge> : null}
                    </div>
                    <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                        {[
                            { label: "复习次数", value: String(card.reps) },
                            { label: "忘记次数", value: String(card.lapses) },
                            {
                                label: "正确率",
                                value:
                                    card.answerCount === 0
                                        ? "—"
                                        : formatPercent(card.correctCount / card.answerCount)
                            },
                            {
                                label: "记忆稳定度",
                                value: card.stability === 0 ? "—" : `${card.stability.toFixed(1)} 天`
                            }
                        ].map((item) => (
                            <div key={item.label} className="rounded-xl bg-surface-sunken px-3 py-2">
                                <dt className="text-xs text-ink-soft">{item.label}</dt>
                                <dd className="text-base font-semibold text-ink tabular-nums">{item.value}</dd>
                            </div>
                        ))}
                    </dl>
                </Card>
            ) : null}
        </div>
    );
}
